import Link from "next/link"

interface CTABannerProps {
  /** Headline text */
  title: string
  /** Supporting description */
  description?: string
  /** Button label */
  buttonText: string
  /** Button link destination */
  buttonHref: string
  /** Background image URL (defaults to a kitchen lifestyle photo) */
  imageUrl?: string
  /** Additional classes on the outer section */
  className?: string
}

/**
 * CTABanner — Full-width call-to-action banner with image background.
 *
 * Dark overlay over a lifestyle image with centered white text and an
 * orange action button. Used at the bottom of all hub page variants.
 */
export function CTABanner({
  title,
  description,
  buttonText,
  buttonHref,
  imageUrl = "https://images.unsplash.com/photo-1541604193435-22287d32c2c2?w=1200&q=80",
  className = "",
}: CTABannerProps) {
  return (
    <section className={`relative rounded-2xl overflow-hidden ${className}`}>
      <img src={imageUrl} alt="" className="absolute inset-0 w-full h-full object-cover" />
      <div className="absolute inset-0 bg-black/65" />
      <div className="relative z-10 text-center py-16 px-10">
        <h2 className="text-2xl md:text-3xl font-black tracking-tight text-white mb-3 drop-shadow-lg">
          {title}
        </h2>
        {description && (
          <p className="text-white/80 text-sm mb-6 max-w-xl mx-auto">{description}</p>
        )}
        <Link
          href={buttonHref}
          className="inline-flex items-center gap-2 bg-[#ff9951] hover:bg-[#f08030] text-[var(--color-brand-action-text)] font-semibold px-7 py-3 rounded-lg transition-colors text-sm shadow-lg"
        >
          {buttonText}
          <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
          </svg>
        </Link>
      </div>
    </section>
  )
}
